import React from "react";
import SeoHead from "@/components/SeoHead";
import ContactForm from "@/components/ContactForm";
import FaqAccordion from "@/components/FaqAccordion";
import { useLang } from "@/lib/useLang";
import { buildFaqSchema } from "@/lib/faq";

export const About = () => {
  const { t, lang } = useLang();

  // When arriving via the footer "FAQ" link (#faq), scroll the FAQ section
  // into view after mount.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash === "#faq") {
      const el = document.getElementById("faq");
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
  }, []);

  const aboutSchema = {
    "@type": "AboutPage",
    "url": "https://thenextcm.com/about",
    "name": "About Kerala Speaks",
    "description": "An independent, non-partisan civic platform for Kerala citizens.",
    "inLanguage": ["en", "ml"],
    "publisher": { "@id": "https://thenextcm.com/#org" },
    "mainEntity": {
      "@type": "Organization",
      "name": "Kerala Speaks",
      "url": "https://thenextcm.com/",
      "email": "keralaspeaks7@gmail.com",
      "areaServed": { "@type": "AdministrativeArea", "name": "Kerala, India" },
      "knowsLanguage": ["en", "ml"],
      "nonprofitStatus": "NonprofitType"
    }
  };

  // SeoHead accepts a single schema or an array — pass both AboutPage and
  // FAQPage so Google picks up the FAQ rich result.
  const pageSchemas = [aboutSchema, buildFaqSchema()];

  return (
    <div className="App min-h-screen" data-testid="about-page">
      <SeoHead
        title={lang === "ml" ? "എന്താണ് കേരളം പറയുന്നു — Kerala Speaks" : "About — Kerala Speaks"}
        description={lang === "ml"
          ? "സ്വതന്ത്ര, പക്ഷപാതരഹിത പൗര വേദി. പ്രസ്സ്/സഹകരണത്തിന്: keralaspeaks7@gmail.com"
          : "Kerala Speaks is an independent, non-partisan civic platform for Kerala citizens. No political affiliation. Contact: keralaspeaks7@gmail.com"}
        canonical="/about"
        schema={pageSchemas}
      />
      <section className="max-w-3xl mx-auto px-5 sm:px-6 md:px-12 pt-10 md:pt-16 pb-20">
        <p
          className="text-xs md:text-sm uppercase font-bold tracking-[0.4em] text-neutral-600 mb-5"
          data-testid="about-eyebrow"
        >
          ★ {t("about_eyebrow")} ★
        </p>
        <h1
          className="font-display uppercase leading-[0.85] tracking-tighter"
          style={{ fontSize: "clamp(2.5rem, 7vw, 5rem)" }}
          data-testid="about-title"
        >
          {t("about_title")}
        </h1>

        <div className="space-y-6 mt-8 text-base md:text-lg font-medium leading-relaxed" data-testid="about-body">
          <p>{t("about_p1")}</p>
          <p>{t("about_p2")}</p>
          <p>{t("about_p3")}</p>
          <p>{t("about_p4")}</p>
        </div>

        <div
          className="brutal-card p-5 md:p-6 mt-10"
          style={{ background: "#FFF7E6" }}
          data-testid="about-civility"
        >
          <p className="text-xs uppercase font-extrabold tracking-[0.3em] mb-2">
            {t("about_civility_label")}
          </p>
          <p className="text-base md:text-lg font-medium leading-relaxed">
            {t("about_civility_body")}
          </p>
        </div>

        {/* FAQ — accordion with first question open. Q&A also emitted as
            FAQPage JSON-LD via SeoHead.schema for rich snippets. */}
        <section className="mt-12" data-testid="about-faq" id="faq">
          <p className="text-xs md:text-sm uppercase font-bold tracking-[0.4em] text-neutral-600 mb-3">
            ★ Frequently Asked Questions ★
          </p>
          <h2
            className="font-display uppercase leading-[0.95] tracking-tighter mb-3"
            style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)" }}
          >
            Questions about Kerala Speaks
          </h2>
          <p className="text-base md:text-lg font-medium leading-relaxed text-neutral-700 mb-6">
            Kerala Speaks is built to help citizens share what matters most. Here are answers to common questions about how the platform works.
          </p>
          <FaqAccordion />
        </section>

        {/* Contact */}
        <div className="mt-10" data-testid="about-contact">
          <ContactForm />
        </div>
      </section>
    </div>
  );
};

export default About;
